import { formatCurrency, getCurrencySymbol, registerLocaleData } from '@angular/common';
import prependCurrencySymbol from '@angular/common/locales/de-AT';
import appendCurrencySymbol from '@angular/common/locales/en-BE';
import { Pipe, PipeTransform } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ComponentService } from '../service/component.service';
import { ShimanoCountryService } from '../service/country/shimano-country.service';

@Pipe({
  name: 'mycurrency',
  standalone: false,
})
export class MycurrencyPipe implements PipeTransform {
  activeCurrency: string;
  constructor(private shimanoCountryService: ShimanoCountryService, private componentService: ComponentService) {
    this.componentService.activeCurrency$.subscribe((currency) =>
      currency ? (this.activeCurrency = currency) : (this.activeCurrency = 'USD')
    );

    switch (this.country) {
      case 'be':
        registerLocaleData(prependCurrencySymbol, 'be');
        break;
      case 'fr':
        registerLocaleData(appendCurrencySymbol, 'fr');
        break;
      case 'lu':
        registerLocaleData(prependCurrencySymbol, 'lu');
        break;
      case 'nl':
        registerLocaleData(prependCurrencySymbol, 'nl');
        break;
      default:
        registerLocaleData(prependCurrencySymbol, this.country);
    }
  }
  get country(): string {
    let country: string;
    this.shimanoCountryService
      .getActive()
      .pipe(take(1))
      .subscribe((data) => (country = data));
    return country;
  }

  get activeCurrency$(): Observable<string> {
    return this.componentService.activeCurrency$;
  }

  transform(value?: number, currencyCode?: string, digitsInfo: string = '1.2-2', locale?: string): string | null {
    (currencyCode = this.activeCurrency), (locale = this.country);
    if (
      // note: the following currency is added on the right side of the amount
      this.country === 'fr' ||
      this.country === 'it' ||
      this.country === 'dk' ||
      this.country === 'se' ||
      this.country === 'fi' ||
      this.country === 'pl' ||
      this.country === 'ee' ||
      this.country === 'lt' ||
      this.country === 'lv' ||
      this.country === 'es' ||
      this.country === 'pt' ||
      this.country === 'sk' ||
      this.country === 'de' ||
      this.country === 'hu' ||
      this.country === 'cz' ||
      this.country === 'ch'
    ) {
      const formatter = new Intl.NumberFormat(
        this.country === 'dk' ||
        this.country === 'se' ||
        this.country === 'ch' ||
        this.country === 'cz' ||
        this.country === 'ee' ||
        this.country === 'pt'
          ? 'fr'
          : this.country,
        {
          style: 'currency',
          currency:
            this.country === 'se'
              ? 'SEK'
              : this.country === 'dk'
              ? 'DKK'
              : this.country === 'hu'
              ? 'HUF'
              : this.country === 'ch'
              ? 'CHF'
              : this.country === 'cz'
              ? this.activeCurrency
              : this.country === 'pl'
              ? 'PLN'
              : 'EUR',
          currencyDisplay: 'narrowSymbol',
        }
      );
      return formatter['formatToParts'](value)
        .map(({ type, value }) => {
          switch (type) {
            case 'group':
              return '.';
            case 'currency':
              if (this.country === 'dk') {
                return value + '.';
              } else if (this.country === 'hu') {
                return 'Ft';
              } else {
                return value;
              }
            default:
              if (type === 'integer') {
                if (value.length === 4) {
                  const data = value;
                  const insertItem = '.';
                  const position = 1;
                  return [data.slice(0, position), insertItem, data.slice(position)].join('');
                }
              }
              return value;
          }
        })
        .reduce((string, part) => string + part);
    } else if (
      // note: the following currency is added on the left side of the amount
      this.country === 'no' ||
      this.country === 'tr' ||
      this.country === 'ie' ||
      this.country === 'gb'
    ) {
      const formatter = new Intl.NumberFormat(this.country === 'no' ? 'tr' : this.country, {
        style: 'currency',
        currency:
          this.country === 'no'
            ? 'NOK'
            : this.country === 'tr'
            ? 'TRY'
            : this.country === 'gb'
            ? 'GBP'
            : this.activeCurrency,
        currencyDisplay: 'narrowSymbol',
      });
      return formatter['formatToParts'](value)
        .map(({ type, value }) => {
          switch (type) {
            case 'group':
              if (this.country === 'ie' || this.country === 'gb') {
                return ',';
              }
              return '.';
            case 'decimal':
              if (this.country === 'ie' || this.country === 'gb') {
                return '.';
              }
              return ',';
            case 'currency':
              if (this.country === 'tr') {
                return '₺';
              } else {
                return value;
              }
            default:
              if (type === 'integer') {
                if (value.length === 4) {
                  const data = value;
                  const insertItem = '.';
                  const position = 1;
                  return [data.slice(0, position), insertItem, data.slice(position)].join('');
                }
              } else if (
                type === 'literal' &&
                (this.country === 'tr' || this.country === 'ie' || this.country === 'gb')
              ) {
                return value.trim();
              }
              return value;
          }
        })
        .reduce((string, part) => string + part);
    } else if (
      this.country === 'sg' ||
      this.country === 'my' ||
      this.country === 'vn' ||
      this.country === 'th' ||
      this.country === 'my' ||
      this.country === 'id' ||
      this.country === 'ph'
    ) {
      const formatter = new Intl.NumberFormat('sg', {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: 'symbol',
      });
      return formatter['formatToParts'](value)
        .map(({ type, value }) => {
          switch (type) {
            case 'group':
              return ',';
            case 'decimal':
              return '.';
            case 'currency':
              if (value.indexOf('US$') === 0) {
                value = '$';
              }
              return 'S' + value;
            default:
              return value;
          }
        })
        .reduce((string, part) => string + part);
    } else if (this.country === 'au' || this.country === 'nz') {
      const formatter = new Intl.NumberFormat(this.country, {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: 'symbol',
      });
      return formatter['formatToParts'](value)
        .map(({ type, value }) => {
          switch (type) {
            case 'group':
              return ',';
            case 'decimal':
              return '.';
            case 'currency':
              if (this.country === 'au') {
                return '$';
              } else if (this.country === 'nz') {
                return '$';
              } else {
                return value;
              }
            default:
              return value;
          }
        })
        .reduce((string, part) => string + part);
    } else if (this.country === 'cn' || this.country === 'jp') {
      const currentCountry = this.country === 'cn' ? `zh-${this.country}` : this.country;
      const formatter = Intl.NumberFormat(currentCountry, {
        style: 'currency',
        currency: this.country === 'cn' ? 'CNY' : 'JPY',
        currencyDisplay: 'symbol',
      });
      return formatter['formatToParts'](value)
        .map(({ type, value }) => {
          switch (type) {
            case 'currency':
              return '¥';
            case 'group':
              return ',';
            case 'decimal':
              return '.';
            default:
              return value;
          }
        })
        .reduce((string, part) => string + part);
    } else if (this.country === 'us' || this.country === 'ca') {
      const formatter = new Intl.NumberFormat('us', {
        style: 'currency',
        currency: this.country === 'us' ? 'USD' : 'CAD',
        currencyDisplay: 'symbol',
      });
      return formatter['formatToParts'](value)
        .map(({ type, value }) => {
          switch (type) {
            case 'group':
              return ',';
            case 'decimal':
              return '.';
            case 'currency':
              return '$ ';
            default:
              return value;
          }
        })
        .reduce((string, part) => string + part);
    } else {
      return formatCurrency(value, locale, getCurrencySymbol(currencyCode, 'wide'), currencyCode, digitsInfo);
    }
  }
}
